import {
  BISHOP,
  BISHOP_DIRS,
  KING,
  KING_DIRS,
  KNIGHT,
  KNIGHT_DIRS,
  makePiece,
  PAWN,
  PAWN_CAPTURES_BY_FACING,
  pieceColor,
  pieceType,
  type PieceType,
  QUEEN,
  QUEEN_DIRS,
  ROOK,
  ROOK_DIRS,
  type Side} from "../board";
import { areEnemies, armourLeft, bindingRoyal, blowOf, disguisedAs, facingAt, kingReach, moatLeft,
  obstructsAt, type Position, royalsOf, structureHealthAt, variantAt, crownedFrom} from "../position";
import { MAX_ATTACK_RANGE, RULES, SLIDER_RANGE, variantLeaps, variantMoves, variantRange,
  VARIANT_LEAP_ORIGINS, VARIANT_MAX_RANGE, VARIANT_OFFSETS } from "../rules";
import { leapCrossesStasis } from "./stasisLeap";

/**
 * Is `sq` attacked by anyone who is not `defender`?
 *
 * Radiates outward from the target rather than iterating the attackers'
 * pieces, which is both faster and the shape check/pin/castling all want.
 *
 * "Not the defender" rather than "this specific side" because that is what
 * every caller has always meant — check, castling through check, and the
 * legality filter all ask whether a square is *unsafe*, not who makes it so.
 * With two armies the two readings coincide; with three, asking about one
 * named enemy silently ignores the other.
 */
/**
 * Does the piece on `at` attack with `pattern`?
 *
 * Its own rank, or the rank it was **crowned from** — an Empress was a queen
 * and royalty was added to what she had, so she takes along a diagonal as well
 * as stepping down one. Movement and attacks have to agree here or she
 * captures out of nowhere in one direction and walks into range in the other:
 * this query is what "is that square safe" asks.
 *
 * **Not `imitating`**, which is deliberately movement-only — a borrowed
 * pattern gives no check and defends no square, and that is what makes Imitate
 * a movement card rather than a second queen.
 *
 * Guarded by `crownedFrom` on an empty map, so every ordinary board pays one
 * size check and no lookup, and perft cannot see it.
 */
const attacksAs = (pos: Position, at: number, type: PieceType, pattern: PieceType): boolean =>
  type === pattern || crownedFrom(pos, at) === pattern;

/**
 * How far the piece on `at` reaches, over every pattern it attacks with.
 *
 * **The range has to come from the pattern, not from the rank.** An Empress is
 * a `KING` piece and `SLIDER_RANGE[KING]` is one, so the rank-and-file guard
 * threw her out at two squares and asked nothing further — she gave check down
 * a diagonal (that loop bounds the ray rather than the piece) and not along a
 * rank, which is the sort of half-working that is worse than neither.
 */
const attackRange = (pos: Position, at: number, type: PieceType): number => {
  const crown = crownedFrom(pos, at);
  const rank = crown ? Math.max(SLIDER_RANGE[type], SLIDER_RANGE[crown]) : SLIDER_RANGE[type];
  // **A row's reach replaces its rank's, in both directions.** It raised once,
  // which was indistinguishable while the only rows were turbo ones; a
  // Confessor reaches five where a bishop reaches eight, and `max` would have
  // this sweep seeing a line she cannot move down — a king refusing a square
  // nothing attacks. `moveGen`'s ray bound reads it exactly the same way.
  const row = variantRange(variantAt(pos, at));
  return row > 0 ? row : rank;
};

/**
 * Does the attacker on `at` still land a blow after plowing `crossed` — the
 * non-obstruction structures between it and the target (§31 S1)? Enemy ones
 * (to the attacker) cost their HP, within `RULES.plowBudget`, and a blow left
 * with nothing is no attack. Must agree with `moveGen`'s `emitRay`.
 */
function plowReaches(pos: Position, at: number, crossed: readonly number[]): boolean {
  const side = pieceColor(pos.board.get(at)!);
  let spent = 0;
  for (const sq of crossed) {
    const owner = pos.structures.get(sq);
    if (owner !== undefined && areEnemies(pos, side, owner)) spent += structureHealthAt(pos, sq);
  }
  return spent <= RULES.plowBudget && blowOf(pos, at) - spent > 0;
}

export function isAttackedByEnemyOf(
  pos: Position,
  sq: number,
  defender: Side
): boolean {
  const board = pos.board;
  // Bounds unpacked into locals and tested inline. This is called once per
  // pseudo-legal move to filter for legality, so it is the hot path of the
  // whole core: a closure per call, or a property load per ray step, is
  // measurable in perft. Empty squares are absent from the map, so a `get`
  // returning undefined *is* "nothing there".
  const cells = pos.cells;
  // A locked piece threatens nothing and still blocks (§26 C4); hoisted.
  const locks = pos.armour.size > 0;
  // A Static Moat, hoisted the same way. Asked of every scan, not just the
  // rays: the rules keep pieces off a wall, but a camp arriving can still be
  // placed on one, and a piece on a wall threatens nothing (§26 C13).
  const moated = pos.moat.size > 0;

  // Pawns. With facing, a pawn's capture squares are no longer a fixed
  // function of its colour, so the old trick of walking the opposite colour's
  // offsets back from the target does not work. Check all four diagonals and
  // ask each pawn found there whether *it* attacks this square.
  // **A variant pawn is skipped here and swept below**, and this is the trap
  // this whole block would otherwise walk into: a Marshal is a `PAWN` piece, so
  // a flat rank test picks it up and prices it with `PAWN_CAPTURES_BY_FACING`.
  // Its diagonals happen to coincide, so the bug is invisible — right answer,
  // wrong reason — until a row whose diagonals differ ships, and it misses the
  // three orthogonals either way. `variants` hoists the map test, so a board
  // with none pays one size check.
  const variants = pos.variant.size > 0;
  for (const offset of BISHOP_DIRS) {
    const from = sq + offset;
    if (!cells.has(from)) continue;
    const piece = board.get(from);
    if (!piece || pieceType(piece) !== PAWN || pieceColor(piece) === defender) continue;
    if (variants && variantMoves(variantAt(pos, from))) continue;
    if (locks && armourLeft(pos, from) > 0) continue;
    if (moated && moatLeft(pos, from) > 0) continue;
    for (const capture of PAWN_CAPTURES_BY_FACING[facingAt(pos, from)]) {
      if (from + capture === sq) return true;
    }
  }

  // Variants that move by their own table. **All eight neighbours, not the
  // five a Marshal can actually reach** — the row is the truth about which
  // squares those are, and a sweep that hard-coded them would be a second
  // definition of the piece that the next row silently invalidates. C13's
  // lesson, from the other side: a guard that reasons from another module's
  // table is a guard that breaks when that table changes.
  if (variants) {
    for (const offset of KING_DIRS) {
      const from = sq + offset;
      if (!cells.has(from)) continue;
      const piece = board.get(from);
      if (!piece || pieceColor(piece) === defender) continue;
      const row = variantAt(pos, from);
      if (!row || !variantMoves(row)) continue;
      if (locks && armourLeft(pos, from) > 0) continue;
      if (moated && moatLeft(pos, from) > 0) continue;
      // `takes` is steps ∪ attacks — everything the row can capture on, which
      // is a longer list than the squares it can *move* to.
      for (const capture of VARIANT_OFFSETS[row][facingAt(pos, from)].takes) {
        if (from + capture === sq) return true;
      }
    }
  }

  for (const offset of KNIGHT_DIRS) {
    const from = sq + offset;
    if (!cells.has(from)) continue;
    if (locks && armourLeft(pos, from) > 0) continue;
    // Cannot be jumped over (§31 C2), movegen's guard exactly.
    if (locks && leapCrossesStasis(pos, from, sq)) continue;
    if (moated && moatLeft(pos, from) > 0) continue;
    const piece = board.get(from);
    // Or crowned from one (§26 C5): a crowned knight is a `KING` piece, so a
    // flat rank test missed its hop.
    if (
      piece &&
      attacksAs(pos, from, pieceType(piece), KNIGHT) &&
      areEnemies(pos, defender, pieceColor(piece))
    ) {
      return true;
    }
  }

  // A row that leaps further than its rank — the Pegasus Knight's (3,2) ring,
  // outside the (2,1) one above. **Its own sweep, because the loop above
  // cannot see it**: this query radiates outward from the square being
  // attacked and stops at the rings it walks, so a piece reaching in from
  // beyond them is check detection going blind — an enemy king steps into the
  // leap and nothing throws.
  //
  // Two steps rather than one table of squares. `VARIANT_LEAP_ORIGINS` says
  // where a leaping piece *could* stand, derived from the rows; the row on the
  // square then answers whether it really reaches here. The alternative is
  // this sweep spelling a row's geometry out itself, which is C13's rule — a
  // guard reasoning from another module's table breaks when that table moves.
  //
  // The leaps are the piece's own, not a rank's, so no `attacksAs`: a crowned
  // Pegasus keeps its ring here exactly as `emitLeaps` gives it one.
  if (variants) {
    for (const offset of VARIANT_LEAP_ORIGINS) {
      const from = sq + offset;
      if (!cells.has(from)) continue;
      if (locks && armourLeft(pos, from) > 0) continue;
      if (moated && moatLeft(pos, from) > 0) continue;
      if (locks && leapCrossesStasis(pos, from, sq)) continue;
      const piece = board.get(from);
      if (!piece || !areEnemies(pos, defender, pieceColor(piece))) continue;
      if (variantLeaps(variantAt(pos, from)).includes(sq - from)) return true;
    }
  }

  for (const offset of KING_DIRS) {
    const from = sq + offset;
    if (!cells.has(from)) continue;
    // A frozen king threatens nothing either (§31 C2: the field takes Monarchs).
    if (locks && armourLeft(pos, from) > 0) continue;
    if (moated && moatLeft(pos, from) > 0) continue;
    const piece = board.get(from);
    if (piece && pieceType(piece) === KING && areEnemies(pos, defender, pieceColor(piece))) {
      return true;
    }
  }

  // Both pieces that attack down a diagonal are capped at the same range, so
  // the sweep itself can stop there — which is also what keeps this query
  // cheap on a board that has grown to hundreds of files.
  // **A turbo row reaches past every entry in that table**, so both bounds open
  // the moment the board holds any variant. Boards with none are untouched.
  const far = variants ? VARIANT_MAX_RANGE : 0;
  const diagonal = Math.max(SLIDER_RANGE[BISHOP], far);
  // The furthest anything can reach from, so a straight ray may stop there.
  const straight = Math.max(MAX_ATTACK_RANGE, far);
  // Buildings on the ray (§31 S1): an obstruction ends it, any other is
  // plowed, and what the attacker spent decides whether it still lands.
  const forts = pos.structures.size !== 0;
  for (const dir of BISHOP_DIRS) {
    let steps = 0;
    let crossed: number[] | null = null;
    for (let from = sq + dir; cells.has(from) && steps < diagonal; from += dir) {
      steps++;
      // And at a moat, the same way (§26 C13).
      if (moated && moatLeft(pos, from) > 0) break;
      const piece = board.get(from);
      if (piece === undefined) {
        if (forts && pos.structures.has(from)) {
          if (obstructsAt(pos, from)) break;
          (crossed ??= []).push(from);
        }
        continue;
      }
      if (crossed !== null && !plowReaches(pos, from, crossed)) break;
      // `break`, never `continue`: read past it and a rook checks through a frozen bishop.
      if (locks && armourLeft(pos, from) > 0) break;
      const type = pieceType(piece);
      // **Asked per piece now, where the rank and file always did.** A
      // Turbo-Bishop is the shared cap breaking — without this, a ray running
      // to thirty reports an *ordinary* bishop nine squares away as attacking.
      if (
        (attacksAs(pos, from, type, BISHOP) || attacksAs(pos, from, type, QUEEN)) &&
        steps <= attackRange(pos, from, type) &&
        areEnemies(pos, defender, pieceColor(piece))
      ) {
        return true;
      }
      // **A king in Long Socks attacks as far as it moves.** Coupled on
      // purpose: captures come out of move generation, so a king that slid to
      // take a piece nobody counted as attacked would be capturing out of
      // nowhere — and the enemy would walk into range because this query is
      // what "is that square safe" asks. The range is the queen's, the same
      // one movegen slides along.
      if (
        type === KING &&
        steps <= kingReach(pos, pieceColor(piece)) &&
        areEnemies(pos, defender, pieceColor(piece))
      ) {
        return true;
      }
      break;
    }
  }

  // The rank and file now stop at the cap too.
  //
  // This is the line that used to run to the edge of the board. The rook was
  // the one uncapped piece, so a blocker at *any* distance might still have
  // been a rook that reached — which meant no query about a square could ever
  // stop early, and the cost of asking "is this square safe" grew with the
  // map. With every slider capped, a ray that has gone `MAX_ATTACK_RANGE`
  // squares cannot find anything that reaches back, so it stops.
  //
  // The old warning still applies to the *shape* of this loop: it asks how
  // far the blocker turned out to be rather than bounding the ray at one
  // piece's range, because rank and file are shared by pieces that could
  // differ. They no longer do, and the check below is what keeps it honest if
  // they ever differ again.
  for (const dir of ROOK_DIRS) {
    let steps = 0;
    let crossed: number[] | null = null;
    for (let from = sq + dir; cells.has(from) && steps < straight; from += dir) {
      steps++;
      if (moated && moatLeft(pos, from) > 0) break;
      const piece = board.get(from);
      if (piece === undefined) {
        if (forts && pos.structures.has(from)) {
          if (obstructsAt(pos, from)) break;
          (crossed ??= []).push(from);
        }
        continue;
      }
      if (crossed !== null && !plowReaches(pos, from, crossed)) break;
      // Blocks, threatens nothing — the diagonal's reason.
      if (locks && armourLeft(pos, from) > 0) break;
      const type = pieceType(piece);
      // Outside the range guard below, because `SLIDER_RANGE[KING]` is one —
      // the socks are what change the reach, not the table.
      if (
        type === KING &&
        steps <= kingReach(pos, pieceColor(piece)) &&
        areEnemies(pos, defender, pieceColor(piece))
      ) {
        return true;
      }
      if (steps <= attackRange(pos, from, type) && areEnemies(pos, defender, pieceColor(piece))) {
        if (attacksAs(pos, from, type, ROOK) || attacksAs(pos, from, type, QUEEN)) return true;
      }
      break;
    }
  }

  // A spy of the defender's own, in an enemy's colours, can check it (§31 C9).
  return pos.disguise.size > 0 && spoofAttacks(pos, sq, defender);
}

/** Re-entry guard: the spoof pass asks the sweep, which would ask it again. */
let spoofing = false;

/**
 * Does a disguised piece of `defender`'s own attack `sq` (§31 C9)?
 *
 * The IFF Spoofer's "can give check to your own Monarchs". Asked by putting
 * the spy in its costume's colours on the board for one sweep and restoring
 * it — the sweep is the one definition of every piece's reach, so a second
 * per-rank copy here would drift. Only reached once the ordinary sweep said
 * no, so a yes is the spy's. A king never spies on itself.
 */
function spoofAttacks(pos: Position, sq: number, defender: Side): boolean {
  if (spoofing) return false;
  for (const from of pos.disguise.keys()) {
    const worn = disguisedAs(pos, from);
    const piece = pos.board.get(from);
    if (!worn || !piece || pieceColor(piece) !== defender || pieceType(piece) === KING) continue;
    if (!areEnemies(pos, defender, worn.side)) continue;
    spoofing = true;
    pos.board.set(from, makePiece(worn.side, pieceType(piece)));
    let hit = false;
    try {
      hit = isAttackedByEnemyOf(pos, sq, defender);
    } finally {
      pos.board.set(from, piece);
      spoofing = false;
    }
    if (hit) return true;
  }
  return false;
}

/**
 * Is this side in check — the binding kind, that has to be answered?
 *
 * **A side holding a spare king is never in check.** Attacking one of two
 * royals is a threat and nothing more: it constrains no move, cannot be mated
 * on, and the army is only back under the ordinary rule once it is down to one
 * king. `threatenedRoyals` is what says a royal is under attack anyway, and it
 * is deliberately not this — every caller here gates a *rule* (re-aim, forge,
 * Sacrifice, the planner's blocking search), and a threat that binds nothing
 * must not fire any of them.
 */
export function isInCheck(pos: Position, side: Side): boolean {
  const king = bindingRoyal(pos, side);
  return king >= 0 && isAttackedByEnemyOf(pos, king, side);
}

/**
 * This side's royal squares that are under attack right now.
 *
 * The check-like message, and **not a `GameStatus`**: it binds nothing, and
 * five sites gate real rules on `status === "check"`, so a status value would
 * fire all of them on a threat the army may legally ignore. A derived read the
 * board can draw and the banner can say, and no rule may consult it.
 */
export function threatenedRoyals(pos: Position, side: Side): number[] {
  return royalsOf(pos, side).filter((sq) => isAttackedByEnemyOf(pos, sq, side));
}

/**
 * Squares holding one of `side`'s pieces that stand between their own king and
 * an enemy slider aimed at it — the only pieces whose *ordinary* move can
 * expose the king.
 *
 * This is what lets the legality filter stop testing every move. Measured
 * before it existed: 15,367 make/unmake-and-ask-the-king cycles per ply, of
 * which 89.8% proved the move legal — nine times in ten the engine played a
 * move, swept twelve rays out from the king, found nothing, and took it back.
 * The whole set is answered here by eight rays, once per position.
 *
 * The claim it rests on is the ordinary one from chess: while not in check,
 * moving a piece that is neither the king nor pinned cannot leave the king
 * attacked. Two moves escape it and are checked the expensive way regardless —
 * a king move, which changes what "safe" means, and an en passant, which
 * removes a pawn from a square the mover never touches and so can open a rank
 * that nothing on this list is standing in.
 *
 * A `Set` rather than a map of pin directions: knowing *which* ray a piece is
 * pinned along would let a move along it be admitted without a test, but pinned
 * pieces are a handful and make/unmake is the answer that cannot be subtly
 * wrong. This narrows the expensive path; it does not try to eliminate it.
 */
export function pinnedAgainst(pos: Position, king: number, side: Side): Set<number> {
  const pinned = new Set<number>();
  if (king < 0) return pinned;
  const board = pos.board;
  const cells = pos.cells;
  // A locked slider pins nothing, and must agree with the sweep above or a pin is missed.
  const locks = pos.armour.size > 0;
  // A moat blocks a pin exactly as it blocks a check (§26 C13).
  const moated = pos.moat.size > 0;
  // The sweep's own bound: a pin it can see and this cannot is a pinned piece
  // taking the cheap path in `generateLegalMoves` and moving off the pin.
  const reach = Math.max(MAX_ATTACK_RANGE, pos.variant.size > 0 ? VARIANT_MAX_RANGE : 0);
  // An obstruction ends a pin's ray; any other building is read as
  // transparent, ignoring the budget (§31 S1) — a superset of the real pins,
  // which only sends more moves down the expensive path.
  const forts = pos.obstructions.size !== 0;

  for (let d = 0; d < QUEEN_DIRS.length; d++) {
    const dir = QUEEN_DIRS[d];
    // `QUEEN_DIRS` is the diagonals *then* the rank and file — see `board.ts`,
    // where the order exists so the two can concatenate. So the tail is the
    // straight rays, and which slider can pin down this one falls out of the
    // index rather than being re-derived from the offset.
    const straight = d >= BISHOP_DIRS.length;

    // The first piece out from the king. Anything that is not ours ends the
    // ray: an enemy there is a checker rather than a pin, and an *ally* there
    // is a blocker we cannot move, so nothing of ours behind it is pinned.
    let blocker = -1;
    let steps = 0;
    let sq = king + dir;
    for (; cells.has(sq) && steps < reach; sq += dir) {
      steps++;
      // **A pin cannot reach through a moat**, and this has to agree with the
      // attack sweep above or the two disagree about the same ray: a pin
      // *missed* is a pinned piece taking the cheap path.
      if (moated && moatLeft(pos, sq) > 0) break;
      if (forts && obstructsAt(pos, sq)) break;
      const piece = board.get(sq);
      if (piece === undefined) continue;
      if (pieceColor(piece) === side) blocker = sq;
      break;
    }
    if (blocker < 0) continue;

    // And the next one behind it, which pins only if it is a slider that
    // travels this ray *and* the king is inside its range — the distance that
    // matters is king-to-slider, because that is the attack the blocker is
    // standing in the way of.
    for (sq = blocker + dir; cells.has(sq) && steps < reach; sq += dir) {
      steps++;
      if (moated && moatLeft(pos, sq) > 0) break;
      if (forts && obstructsAt(pos, sq)) break;
      const piece = board.get(sq);
      if (piece === undefined) continue;
      if (locks && armourLeft(pos, sq) > 0) break;
      const type = pieceType(piece);
      const reaches = straight
        ? attacksAs(pos, sq, type, ROOK) || attacksAs(pos, sq, type, QUEEN)
        : attacksAs(pos, sq, type, BISHOP) || attacksAs(pos, sq, type, QUEEN);
      // `attackRange`, not `SLIDER_RANGE[type]`: the sweep's question, so the
      // sweep's answer. Picks up the crown this line was quietly missing.
      if (
        reaches &&
        steps <= attackRange(pos, sq, type) &&
        areEnemies(pos, side, pieceColor(piece))
      ) {
        pinned.add(blocker);
      }
      break;
    }
  }

  return pinned;
}
